import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Form } from "react-bootstrap";
import classNames from "classnames";
import addToMailchimp from "gatsby-plugin-mailchimp";

import RichTextBlock from "./RichTextBlock";
import InvisibleReCAPTCHA from "./InvisibleReCAPTCHA";

import { isValidEmail } from "../utils/utils";

import "../styles/components/NewsletterSubscriptionForm.scss";

const NewsletterSubscriptionForm = ({
    variant = "regular",
    lead,
    successMessage,
    ...props
}) => {
    const [email, setEmail] = useState("");

    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [submissionError, setSubmissionError] = useState(null);

    const updateEmail = e => {
        setEmail(e.target.value);
        setError(!isValidEmail(e.target.value));
    };

    const isValid = () => {
        setError(!isValidEmail(email));
        return !error;
    };

    const submitForm = reCAPTCHAToken => {
        setSubmitting(true);
        if (reCAPTCHAToken && isValid()) {
            addToMailchimp(email, { SOURCE: "Blog" })
                .then(data => {
                    setSubmissionError(
                        data.result === "success" ? null : data.msg
                    );
                    setSubmitted(true);
                })
                .catch(error => console.error(error));
        }
        reCAPTCHARef.current.reset();
        setSubmitting(false);
    };

    const reCAPTCHARef = useRef(null);

    const executeReCAPTCHA = async e => {
        e.preventDefault();
        const token = await reCAPTCHARef.current.executeAsync();
        submitForm(token);
    };

    if (submitted && !submissionError) {
        return (
            <Alert variant="success">
                {successMessage ? (
                    <RichTextBlock content={successMessage} />
                ) : (
                    "Thank you for subscribing! We will be in touch soon."
                )}
            </Alert>
        );
    }

    const buttonVariant = () => {
        if (variant === "extended") {
            return "outline-white";
        } else if (variant === "stacked") {
            return "outline-primary";
        } else {
            return "white";
        }
    };

    return (
        <Form
            className={classNames(
                "newsletter-subscription-form",
                variant,
                "d-flex",
                "align-items-center",
                "justify-content-between",
                {
                    "flex-column": variant === "stacked",
                }
            )}
            onSubmit={executeReCAPTCHA}
        >
            {lead && (
                <div className="form-lead">
                    <RichTextBlock content={lead} />
                </div>
            )}
            <Form.Control
                type="email"
                required
                value={email}
                onChange={updateEmail}
                placeholder="Subscribe for updates..."
                className={classNames({
                    transparent: variant === "regular",
                    invalid: !!error,
                })}
            />
            <Button
                variant={buttonVariant()}
                type="submit"
                disabled={submitting}
                className={classNames("submit-form-button", "text-nowrap", {
                    disabled: submitting,
                })}
            >
                Sign Up
            </Button>
            <InvisibleReCAPTCHA innerRef={reCAPTCHARef} />
        </Form>
    );
};

NewsletterSubscriptionForm.propTypes = {
    lead: PropTypes.shape({ raw: PropTypes.string }),
    successMessage: PropTypes.shape({ raw: PropTypes.string }),
    variant: PropTypes.oneOf(["regular", "extended", "stacked"]),
};

export default NewsletterSubscriptionForm;
